// src/services/taskApiService.js
import {
	collection,
	query,
	where,
	onSnapshot,
	Timestamp,
	orderBy,
	limit,
} from 'firebase/firestore';
import { db } from '@/firebase/config';
import useAuthStore from '@/store/useAuthStore';
import { createAdaptiveDebounce } from '@/utils/adaptiveDebounce';

// Enhanced in-memory cache with LRU and TTL
export const taskCache = {
	data: new Map(),
	maxSize: 50, // Maximum number of cached entries
	ttl: 5 * 60 * 1000, // 5 minutes TTL

	createKey: (userId, viewType, date) => {
		const dateStr = date.toISOString().split('T')[0];
		return `${userId}-${viewType}-${dateStr}`;
	},

	get: function (key) {
		const cached = this.data.get(key);
		if (!cached) return null;

		// Check if cache is still valid
		if (Date.now() - cached.timestamp > this.ttl) {
			this.data.delete(key);
			return null;
		}

		// Move to end (LRU)
		this.data.delete(key);
		this.data.set(key, cached);

		return cached.data;
	},

	set: function (key, data) {
		// Remove oldest entry if at capacity
		if (this.data.size >= this.maxSize && !this.data.has(key)) {
			const firstKey = this.data.keys().next().value;
			this.data.delete(firstKey);
		}

		this.data.set(key, {
			data,
			timestamp: Date.now(),
		});
	},

	clear: function () {
		this.data.clear();
	},
};

// Get date range based on view type
const getDateRange = (date, viewType) => {
	const start = new Date(date);
	const end = new Date(date);

	switch (viewType) {
		case 'Day':
			start.setHours(0, 0, 0, 0);
			end.setHours(23, 59, 59, 999);
			break;

		case 'Week':
			// Start on Monday
			const day = start.getDay();
			const diff = day === 0 ? 6 : day - 1;
			start.setDate(start.getDate() - diff);
			start.setHours(0, 0, 0, 0);

			end.setDate(start.getDate() + 6);
			end.setHours(23, 59, 59, 999);
			break;

		case 'Month':
			start.setDate(1);
			start.setHours(0, 0, 0, 0);

			end.setMonth(end.getMonth() + 1, 0);
			end.setHours(23, 59, 59, 999);
			break;

		case 'Year':
			start.setMonth(0, 1);
			start.setHours(0, 0, 0, 0);

			end.setMonth(11, 31);
			end.setHours(23, 59, 59, 999);
			break;

		case 'Unplanned':
			// For unplanned, we only want tasks without dates
			return { start: null, end: null };

		default:
			throw new Error(`Invalid view type: ${viewType}`);
	}

	return { start, end };
};

// Convert Firestore Timestamp to Date
const timestampToDate = (timestamp) => {
	if (!timestamp) return null;
	if (timestamp.seconds) {
		return new Date(timestamp.seconds * 1000);
	}
	if (timestamp instanceof Date) {
		return timestamp;
	}
	return new Date(timestamp);
};

// Subscribe to tasks in date range
// src/services/taskApiService.js

export const subscribeToTasksInRange = (userId, date, viewType, callback) => {
	if (!userId) {
		callback({
			overdueItems: [],
			inRangeItems: [],
			completedItems: [],
			labels: [],
			error: null,
		});
		return () => {};
	}

	const { start, end } = getDateRange(date, viewType);

	// Create a date object for the beginning of today for the overdue check.
	// This ensures tasks due today are not considered overdue.
	const startOfToday = new Date();
	startOfToday.setHours(0, 0, 0, 0);
	let effectiveAppointmentStart = start;
	// For appointments, we want to show ALL appointments in the view range,
	// not just from today. Remove the restriction that was limiting past appointments.
	// Keep the original start date for appointments to include past appointments
	const allQueries = [];

	// Dynamic limits based on view type - Year/Month views need higher limits
	const getQueryLimit = (baseLimit) => {
		switch (viewType) {
			case 'Year':
				return baseLimit * 5; // 1000 for tasks, 500 for completed
			case 'Month':
				return baseLimit * 2; // 400 for tasks, 200 for completed
			default:
				return baseLimit;
		}
	};

	// --- REQUIREMENT 1: Static Overdue Query ---
	// Only fetch overdue tasks if the view is NOT 'Unplanned'
	if (viewType !== 'Unplanned') {
		allQueries.push({
			purpose: 'overdue',
			collection: 'tasks',
			query: query(
				collection(db, 'tasks'),
				where('userId', '==', userId),
				where('isCompleted', '==', false),
				where('dueDateTimeUTC', '<', Timestamp.fromDate(startOfToday)), // Use startOfToday here
				orderBy('dueDateTimeUTC', 'desc')
			),
			type: 'Tasks',
		});
	}

	// --- REQUIREMENT 2: Dynamic In-Range Queries ---
	if (viewType === 'Unplanned') {
		allQueries.push({
			purpose: 'inRange',
			collection: 'tasks',
			query: query(
				collection(db, 'tasks'),
				where('userId', '==', userId),
				where('isCompleted', '==', false),
				where('dueDateTimeUTC', '==', null),
				limit(100) // Limit unplanned tasks
			),
			type: 'Tasks',
		});
	} else {
		// Uncompleted tasks due in the current range
		allQueries.push({
			purpose: 'inRange',
			collection: 'tasks',
			query: query(
				collection(db, 'tasks'),
				where('userId', '==', userId),
				where('isCompleted', '==', false),
				where('dueDateTimeUTC', '>=', Timestamp.fromDate(start)),
				where('dueDateTimeUTC', '<=', Timestamp.fromDate(end)),
				orderBy('dueDateTimeUTC', 'asc'),
				limit(getQueryLimit(200))
			),
			type: 'Tasks',
		});
		// Appointments starting in the current range
		allQueries.push({
			purpose: 'appointmentsInRange',
			collection: 'appointments',
			query: query(
				collection(db, 'appointments'),
				where('userId', '==', userId),
				where(
					'startDateTimeUTC',
					'>=',
					Timestamp.fromDate(effectiveAppointmentStart)
				),
				where('startDateTimeUTC', '<=', Timestamp.fromDate(end)),
				orderBy('startDateTimeUTC', 'asc'),
				limit(getQueryLimit(200))
			),
			type: 'Appointments',
		});
	}

	// --- Completed Tasks Queries ---
	if (viewType === 'Unplanned') {
		allQueries.push({
			purpose: 'completedInRange',
			collection: 'tasks',
			query: query(
				collection(db, 'tasks'),
				where('userId', '==', userId),
				where('isCompleted', '==', true),
				where('dueDateTimeUTC', '==', null),
				limit(100)
			),
			type: 'Tasks',
		});
	} else if (viewType === 'Day') {
		// For Day view, fetch completed tasks based on completionDateUTC
		allQueries.push({
			purpose: 'completedInRange', // Reusing this purpose for consistency in processResults
			collection: 'tasks',
			query: query(
				collection(db, 'tasks'),
				where('userId', '==', userId),
				where('isCompleted', '==', true),
				where('completionDateUTC', '>=', Timestamp.fromDate(start)),
				where('completionDateUTC', '<=', Timestamp.fromDate(end)),
				orderBy('completionDateUTC', 'desc'), // Order by completion date
				limit(100)
			),
			type: 'Tasks',
		});
	} else {
		// Week, Month, Year views
		// Fetch completed tasks based on their original due date
		allQueries.push({
			purpose: 'completedInRange',
			collection: 'tasks',
			query: query(
				collection(db, 'tasks'),
				where('userId', '==', userId),
				where('isCompleted', '==', true),
				where('dueDateTimeUTC', '>=', Timestamp.fromDate(start)),
				where('dueDateTimeUTC', '<=', Timestamp.fromDate(end)),
				orderBy('dueDateTimeUTC', 'desc'),
				limit(getQueryLimit(100))
			),
			type: 'Tasks',
		});

		allQueries.push({
			purpose: 'completedInPeriod',
			collection: 'tasks',
			query: query(
				collection(db, 'tasks'),
				where('userId', '==', userId),
				where('isCompleted', '==', true),
				where('completionDateUTC', '>=', Timestamp.fromDate(start)),
				where('completionDateUTC', '<=', Timestamp.fromDate(end)),
				orderBy('completionDateUTC', 'desc'),
				limit(getQueryLimit(100))
			),
			type: 'Tasks',
		});
	}

	let results = new Map();
	const listenerTracker = new Set(
		allQueries.map((q) => {
			if (q.purpose === 'inRange' && q.collection === 'appointments')
				return 'appointmentsInRange';
			return q.purpose;
		})
	);
	const completedListeners = new Set();
	let lastResult = null; // Store last result to prevent duplicate callbacks

	const processResults = () => {
		// ... (The rest of the function remains unchanged)
		if (completedListeners.size < listenerTracker.size) return;

		try {
			const overdueItems =
				results
					.get('overdue')
					?.map((doc) => ({ id: doc.id, type: 'Tasks', ...doc.data() })) || [];
			const inRangeTasks =
				results
					.get('inRange')
					?.map((doc) => ({ id: doc.id, type: 'Tasks', ...doc.data() })) || [];
			const inRangeAppts =
				results.get('appointmentsInRange')?.map((doc) => ({
					id: doc.id,
					type: 'Appointments',
					...doc.data(),
				})) || [];
			const completedInRangeFromDB =
				results
					.get('completedInRange')
					?.map((doc) => ({ id: doc.id, type: 'Tasks', ...doc.data() })) || [];
			const completedTodayFromDB =
				results
					.get('completedToday')
					?.map((doc) => ({ id: doc.id, type: 'Tasks', ...doc.data() })) || [];
			const completedInPeriodFromDB =
				results
					.get('completedInPeriod')
					?.map((doc) => ({ id: doc.id, type: 'Tasks', ...doc.data() })) || [];

			const inRangeItems = [...inRangeTasks, ...inRangeAppts];

			// Combine completed tasks: those due in range + those completed today + those completed in period
			const completedItemsMap = new Map();

			// Add tasks that were due in the range and completed
			completedInRangeFromDB.forEach((item) =>
				completedItemsMap.set(item.id, item)
			);

			// Add tasks that were completed today (even if due outside the range)
			// This ensures tasks completed today appear in the Day view
			completedTodayFromDB.forEach((item) =>
				completedItemsMap.set(item.id, item)
			);

			// Add tasks that were completed within the period (Week/Month/Year views)
			// This ensures overdue tasks marked as completed appear in the current period
			completedInPeriodFromDB.forEach((item) =>
				completedItemsMap.set(item.id, item)
			);

			const completedItems = Array.from(completedItemsMap.values());

			const allUncompleted = [...overdueItems, ...inRangeItems];
			const allItemsMap = new Map();
			allUncompleted.forEach((item) => allItemsMap.set(item.id, item));
			const finalItemsArray = Array.from(allItemsMap.values());

			const labelsSet = new Set();
			finalItemsArray.forEach((item) => {
				if (item.labels?.[0]) labelsSet.add(item.labels[0]);
			});
			completedItems.forEach((item) => {
				if (item.labels?.[0]) labelsSet.add(item.labels[0]);
			});

			finalItemsArray.sort(
				(a, b) =>
					(timestampToDate(a.dueDateTimeUTC || a.startDateTimeUTC) || 0) -
					(timestampToDate(b.dueDateTimeUTC || b.startDateTimeUTC) || 0)
			);
			completedItems.sort(
				(a, b) =>
					(timestampToDate(a.dueDateTimeUTC || a.startDateTimeUTC) || 0) -
					(timestampToDate(b.dueDateTimeUTC || b.startDateTimeUTC) || 0)
			);

			// Cache the last result to prevent unnecessary callbacks with identical data
			const newResult = {
				items: finalItemsArray,
				completedItems: completedItems,
				labels: Array.from(labelsSet),
				error: null,
			};

			// Only call callback if data actually changed
			// Performance note: JSON.stringify is used for deep comparison to prevent unnecessary re-renders.
			// For large datasets, consider using a specialized deep-equality library (e.g., fast-deep-equal)
			// for better performance if this becomes a bottleneck.
			const resultString = JSON.stringify(newResult);
			if (!lastResult || lastResult !== resultString) {
				lastResult = resultString;
				callback(newResult);
			}
		} catch (error) {
			console.error('Error processing tasks data:', error);
			callback({
				items: [],
				completedItems: [],
				labels: [],
				error: error.message,
			});
		}
	};

	// Use adaptive debouncing to handle both single updates and bulk recurrent operations
	const debouncedProcessResults = createAdaptiveDebounce(processResults, {
		normalDelay: 1000,
		bulkDelay: 3000,
		rapidThreshold: 200,
		bulkThreshold: 4,
		debugLabel: 'taskApiService',
	});

	const unsubscribes = allQueries.map((q) => {
		let key = q.purpose;
		if (key === 'inRange' && q.collection === 'appointments') {
			key = 'appointmentsInRange';
		}

		return onSnapshot(
			q.query,
			(snapshot) => {
				results.set(key, snapshot.docs);
				completedListeners.add(key);
				debouncedProcessResults();
			},
			(err) => {
				console.error(`Error in ${key} listener:`, err);
				results.set(key, []);
				completedListeners.add(key);
				debouncedProcessResults();
			}
		);
	});

	return () => unsubscribes.forEach((unsub) => unsub());
};

// Fetch tasks for a specific date range (one-time fetch)
export const fetchTasksInRange = async (userId, date, viewType) => {
	if (!userId) {
		return { items: [], completedItems: [], labels: [] };
	}

	// For now, we'll use the subscription method and return a promise
	return new Promise((resolve) => {
		const unsubscribe = subscribeToTasksInRange(
			userId,
			date,
			viewType,
			(result) => {
				unsubscribe();
				resolve(result);
			}
		);
	});
};
